import { useCallback, useEffect, useState } from 'react'

/**
 * Hook to check internet connectivity by testing external connections
 * Tests connectivity to multiple reliable endpoints
 */
export const useInternetConnectivity = () => {
  const [isConnected, setIsConnected] = useState(false)
  const [isChecking, setIsChecking] = useState(true)
  const [lastCheck, setLastCheck] = useState(null)

  const checkConnectivity = useCallback(async () => {
    setIsChecking(true)
    
    // Test multiple endpoints for reliability, prioritizing Revatek Connect
    const testEndpoints = [
      'https://connect.revatek.com/',
    ]
    
    let connected = false
    
    for (const endpoint of testEndpoints) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout
        
        const _response = await fetch(endpoint, {
          method: 'HEAD',
          mode: 'no-cors', // Avoid CORS issues
          signal: controller.signal,
          cache: 'no-cache',
        })
        
        clearTimeout(timeoutId)
        
        // If we get any response (even opaque), we have connectivity
        connected = true
        break
      } catch (_error) {
        // Try next endpoint
        continue
      }
    }
    
    setIsConnected(connected)
    setLastCheck(new Date())
    setIsChecking(false)
    
    return connected
  }, [])

  // Check connectivity on mount
  useEffect(() => {
    checkConnectivity()
  }, [checkConnectivity])

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(checkConnectivity, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [checkConnectivity])

  return {
    isConnected,
    isChecking,
    lastCheck,
    refresh: checkConnectivity,
  }
}
